import React, { useRef, useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { MoreVertical, Upload, Download, ChevronDown, LayoutList } from 'lucide-react';
import SubHeader from '../../../../components/common/SubHeader/SubHeader';

const LeadHeader = ({
    searchQuery,
    setSearchQuery,
    totalLeads,
    viewMode,
    setViewMode,
    selectedCount = 0,
    onClearSelection,
    onMassAction,
    isAllSelectedAcrossPages,
    setIsAllSelectedAcrossPages,
    advanceFilterFields = [],
    onAdvanceFilterSubmit = null,
    onCustomSearchSubmit = null,
    onCustomSearchClear = null,
    onExport = null,
    onCustomDownload = null,
    onAddLead = null,
    customSearchFetcher = null,
    // New: list-level controls
    itemsPerPage = '20',
    onItemsPerPageChange = null,
    onExportLeads = null,
    onImportLeads = null,
    onCustomFieldsClick = null,
    permissions = null, // Added permissions prop
}) => {
    const [showMoreActions, setShowMoreActions] = useState(false);
    const moreActionsRef = useRef(null);

    const [showListSettings, setShowListSettings] = useState(false);
    const listSettingsRef = useRef(null);

    // ── Permission helpers ───────────────────────────────────────────────────
    const canAdd = permissions ? permissions.Add !== false : true;
    const canEdit = permissions ? permissions.Edit !== false : true;
    const canView = permissions ? permissions.View !== false : true;
    const canDelete = permissions ? permissions.Delete !== false : true;
    const canSearch = permissions ? permissions.Search !== false : true;
    const canImport = permissions ? permissions.Import !== false : true;
    const canExport = permissions ? permissions.Export !== false : true;
    const canMassUpdate = permissions ? permissions.MassUpdate !== false : true;
    const canMassDelete = permissions ? permissions.MassDelete !== false : true;
    const canAddCustomField = permissions ? permissions.AddCustomField !== false : true;

    // Mass / individual operations
    const canAddTask = permissions ? permissions.AddTask !== false : true;
    const canAddAppointment = permissions ? permissions.AddAppointment !== false : true;
    const canAddFollowup = permissions ? permissions.AddFollowup !== false : true;
    const canSendSms = permissions ? permissions.SendSms !== false : true;
    const canSendMail = permissions ? permissions.SendMail !== false : true;
    const canSendVoice = permissions ? permissions.SendVoice !== false : true;
    const canMassAssign = permissions ? permissions.MassAssign !== false : true;
    const canMassSMS = permissions ? permissions.MassSMS !== false : true;
    const canMassEmail = permissions ? permissions.MassEmail !== false : true;
    const canMassBroadcast = permissions ? permissions.MassBroadcast !== false : true;
    const canSpecial1 = permissions ? permissions.Special1 !== false : true;

    // Click-outside: mass actions dropdown
    useEffect(() => {
        const handler = (e) => {
            if (moreActionsRef.current && !moreActionsRef.current.contains(e.target)) {
                setShowMoreActions(false);
            }
        };
        if (showMoreActions) {
            document.addEventListener('mousedown', handler);
            return () => document.removeEventListener('mousedown', handler);
        }
    }, [showMoreActions]);

    // Click-outside: list settings dropdown
    useEffect(() => {
        const handler = (e) => {
            if (listSettingsRef.current && !listSettingsRef.current.contains(e.target)) {
                setShowListSettings(false);
            }
        };
        if (showListSettings) {
            document.addEventListener('mousedown', handler);
            return () => document.removeEventListener('mousedown', handler);
        }
    }, [showListSettings]);

    // ⋮ three-dot dropdown — Items Per Page, Import, Export, Column Search
    // Rendered as customMenuItems inside the SubHeader expanded bar (left of refresh)
    const listSettingsDropdown = (
        <div className="relative" ref={listSettingsRef}>
            <button
                onClick={() => setShowListSettings(!showListSettings)}
                className="subheader-action-btn"
                title="List Settings"
            >
                <MoreVertical size={18} />
            </button>

            {showListSettings && (
                <div className="absolute left-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-xl z-[1010]">
                    {/* Items Per Page */}
                    <div className="px-4 py-3 border-b border-gray-100">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                            Items Per Page
                        </label>
                        <div className="relative">
                            <select
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400 appearance-none pr-8 cursor-pointer"
                                value={itemsPerPage}
                                onChange={(e) => {
                                    if (onItemsPerPageChange) onItemsPerPageChange(e.target.value);
                                    setShowListSettings(false);
                                }}
                            >
                                <option value="20">20</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                    </div>

                    {/* Import */}
                    {canImport && (
                        <button
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition text-sm text-gray-700 border-b border-gray-100"
                            onClick={() => {
                                if (onImportLeads) onImportLeads();
                                setShowListSettings(false);
                            }}
                        >
                            <Upload className="w-4 h-4 text-gray-500" />
                            <span>Import</span>
                        </button>
                    )}

                    {/* Export */}
                    {canExport && (
                        <button
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition text-sm text-gray-700 border-b border-gray-100"
                            onClick={() => {
                                if (onExportLeads) onExportLeads();
                                setShowListSettings(false);
                            }}
                        >
                            <Download className="w-4 h-4 text-gray-500" />
                            <span>Export</span>
                        </button>
                    )}

                    {/* Column Search — triggers custom search panel */}
                    {canSearch && (
                        <button
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition text-sm text-gray-700 border-b border-gray-100"
                            onClick={() => {
                                // Trigger custom search (already wired to onCustomSearchSubmit)
                                setShowListSettings(false);
                            }}
                        >
                            <LayoutList className="w-4 h-4 text-gray-500" />
                            <span>Column Search</span>
                        </button>
                    )}

                    {/* Custom Fields */}
                    {canAddCustomField && (
                        <button
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition text-sm text-gray-700"
                            onClick={() => {
                                if (onCustomFieldsClick) onCustomFieldsClick();
                                setShowListSettings(false);
                            }}
                        >
                            <Icon icon="mdi:wrench" className="w-4 h-4 text-gray-500" />
                            <span>Custom Fields</span>
                        </button>
                    )}
                </div>
            )}
        </div>
    );

    const massPanel = (
        <div className="w-full flex items-center justify-between bg-white px-2">
            <div className="flex items-center gap-3">
                {canMassAssign && (
                    <button
                        onClick={() => onMassAction('followup')}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition"
                    >
                        AssignTo
                    </button>
                )}
                {canMassSMS && (
                    <button
                        onClick={() => onMassAction('sms')}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition"
                    >
                        Send SMS
                    </button>
                )}
                {canMassEmail && (
                    <button
                        onClick={() => onMassAction('mail')}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition"
                    >
                        Send Mail
                    </button>
                )}
                {canMassBroadcast && (
                    <button
                        onClick={() => onMassAction('voice')}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition"
                    >
                        Voice Broadcast
                    </button>
                )}
                {canMassUpdate && (
                    <button
                        onClick={() => onMassAction('mass_update')}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition"
                    >
                        Update
                    </button>
                )}

                <div className="relative" ref={moreActionsRef}>
                    <button
                        onClick={() => setShowMoreActions(!showMoreActions)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition flex items-center gap-1"
                    >
                        More actions
                        <Icon icon={showMoreActions ? 'tabler:chevron-up' : 'tabler:chevron-down'} fontSize={18} />
                    </button>
                    {showMoreActions && (
                        <div className="absolute left-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-[1001]">
                            <div className="py-1">
                                {[
                                    { id: 'addtask', label: 'Add Task', show: canAddTask },
                                    { id: 'addappointment', label: 'Add Appointment', show: canAddAppointment },
                                    { id: 'marketing_sequence', label: 'Marketing Sequence', show: canSpecial1 },
                                    { id: 'international_sms', label: 'Send International SMS', show: canSendSms },
                                    { id: 'add_tag', label: 'Add Tag', show: canEdit },
                                    { id: 'remove_tag', label: 'Remove Tag', show: canEdit },
                                    { id: 'add_call_list', label: 'Add Call List', show: canEdit },
                                ].filter(x => x.show).map(action => (
                                    <button
                                        key={action.id}
                                        onClick={() => { onMassAction(action.id); setShowMoreActions(false); }}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition"
                                    >
                                        {action.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-4">
                <div className="bg-blue-50 border border-blue-100 rounded px-3 py-2 text-sm text-blue-700 font-medium">
                    {isAllSelectedAcrossPages ? (
                        <span>All {totalLeads} leads selected</span>
                    ) : (
                        <span>{selectedCount} of {totalLeads} leads selected</span>
                    )}
                </div>

                {selectedCount > 0 && totalLeads > selectedCount && (
                    <div className="bg-amber-50 border border-amber-100 rounded px-3 py-2 text-sm text-amber-800 flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="apply-all-checkbox"
                            checked={isAllSelectedAcrossPages}
                            onChange={(e) => setIsAllSelectedAcrossPages(e.target.checked)}
                            className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                        />
                        <label htmlFor="apply-all-checkbox" className="cursor-pointer select-none font-medium">
                            Apply on all <span className="font-bold">{totalLeads}</span> leads
                        </label>
                    </div>
                )}

                {canMassDelete && (
                    <button
                        onClick={() => onMassAction('delete')}
                        className="text-red-600 hover:text-red-700 transition flex items-center gap-1.5 font-medium px-2"
                    >
                        <Icon icon="tabler:trash" fontSize={20} />
                        Delete
                    </button>
                )}

                <button
                    onClick={onClearSelection}
                    className="p-1.5 hover:bg-gray-100 rounded-full transition text-gray-400 hover:text-gray-600"
                    title="Clear Selection"
                >
                    <Icon icon="tabler:x" fontSize={22} />
                </button>
            </div>
        </div>
    );

    const customMenuItems = (
        <div className="flex items-center gap-1">
            {listSettingsDropdown}
        </div>
    );

    return (
        <header className="sticky top-0 z-[1001] bg-white border-b border-gray-200">
            <SubHeader
                pageName="Leads"
                pageCode="PAG10031"
                title="Leads"
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                searchPlaceholder="Search leads..."
                viewMode={viewMode}
                setViewMode={setViewMode}
                showViewSwitcher={true}
                showGrid={true}
                showTable={true}
                showCalendar={false}
                showSettings={true}
                showAdvanceFilter={canSearch}
                showSearch={canSearch}
                showExport={false}
                showCustomExport={false}
                massActionPanel={selectedCount > 0 ? massPanel : null}
                totalCount={totalLeads}
                advanceFilterFields={advanceFilterFields}
                onAdvanceFilterSubmit={onAdvanceFilterSubmit}
                showCustomSearch={canSearch}
                onCustomSearchSubmit={onCustomSearchSubmit}
                onCustomSearchClear={onCustomSearchClear}
                customSearchFetcher={customSearchFetcher}
                showRefresh={true}
                onRefreshClick={() => window.location.reload()}
                onAddClick={canAdd ? onAddLead : null}
                showAddInMenu={false}
                customMenuItems={customMenuItems}
            />
        </header>
    );
};


export default LeadHeader;
